import { store } from '../state';
//import { getConfigOptional } from '../config';
import { i18n } from '../i18n';
import { renderTemplate } from '../template-engine';
import Chart from 'chart.js/auto';
import 'chartjs-adapter-date-fns';

// Импорт шаблона
import siteInfoTpl from '../templates/site-card.html?raw';

const els = {
  siteDetails: document.getElementById('site-details') as HTMLElement,
  siteInfo: document.getElementById('site-info') as HTMLElement,
  siteParamSelect: document.getElementById('site-param-select') as HTMLSelectElement,
  siteChartContainer: document.getElementById('site-chart-container') as HTMLElement,
  siteChartCanvas: document.getElementById('site-chart') as HTMLCanvasElement,
  siteLoading: document.getElementById('site-loading') as HTMLElement,
};

let siteChart: Chart | null = null;

// ============================================================================
// Конвертация HTML-тегов в Unicode
// ============================================================================

function htmlToUnicode(str: string): string {
  return str
    .replace(/<sub>(\d+)<\/sub>/g, (_, digits) => {
      const subscriptMap: Record<string, string> = {
        '0': '₀', '1': '₁', '2': '₂', '3': '₃', '4': '₄',
        '5': '₅', '6': '₆', '7': '₇', '8': '₈', '9': '₉'
      };
      return digits.split('').map((d: string) => subscriptMap[d] || d).join('');
    })
    .replace(/<sup>(\d+)<\/sup>/g, (_, digits) => {
      const superscriptMap: Record<string, string> = {
        '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴',
        '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹',
        '+': '⁺', '-': '⁻'
      };
      return digits.split('').map((d: string) => superscriptMap[d] || d).join('');
    })
    .replace(/<sub>([^<]+)<\/sub>/gi, '_$1_')
    .replace(/<sup>([^<]+)<\/sup>/gi, '^$1^');
}

function getParameterName(code: string): string {
  const state = store.getState();
  const dict = state.parametersDict;
  
  if (!dict || !Array.isArray(dict)) return code;
  
  const param = dict.find((p: any) => 
    p.code === code || p.id === code || p.name === code
  );
  
  const rawName = param?.name || code;
  return htmlToUnicode(rawName);
}

// ============================================================================
// Рендеринг панели
// ============================================================================

export function renderSitePanel(state: any): void {
  const site = state.selectedSite;
  const tsData = state.siteTimeSeries;
  const selectedParam = state.selectedSiteParameter;
  const isLoading = state.siteTimeSeriesLoading;

  if (!site) return;

  els.siteDetails.style.display = 'block';

  // Рендерим шаблон с информацией о пункте мониторинга
  const templateData = {
    name: site.name || i18n.t('ui.unnamed'),
    description: site.description || ''
  };
  els.siteInfo.innerHTML = renderTemplate(siteInfoTpl, templateData);

  // Состояние загрузки
  if (isLoading) {
    els.siteLoading.style.display = 'block';
    els.siteChartContainer.style.display = 'none';
    els.siteParamSelect.style.display = 'none';
    return;
  } else {
    els.siteLoading.style.display = 'none';
    els.siteChartContainer.style.display = 'block';
    els.siteParamSelect.style.display = 'block';
  }

  // Заполнение комбобокса
  if (tsData && tsData.columns) {
    const params: string[] = tsData.columns.filter((c: string) => c !== 'date' && c !== 'sample_id');
    els.siteParamSelect.innerHTML = params.map((p: string) => {
      const displayName = getParameterName(p);
      return `<option value="${p}" ${p === selectedParam ? 'selected' : ''}>${displayName}</option>`;
    }).join('');
  }

  // Построение графика
  updateSiteChart(tsData, selectedParam);
}

export function initSitePanel(): void {
  console.log('🔧 Инициализация панели пункта мониторинга');
  
  els.siteParamSelect.addEventListener('change', (e) => {
    const param = (e.target as HTMLSelectElement).value;
    store.dispatch({ type: 'SET_SELECTED_SITE_PARAMETER', payload: param });
  });
}

// ============================================================================
// Построение графика
// ============================================================================

function updateSiteChart(tsData: any, param: string): void {
  if (!tsData || !param || !tsData.rows?.length) {
    if (siteChart) siteChart.destroy();
    return;
  }

  const points = tsData.rows
    .map((row: any) => {
      const val = row[param];
      return {
        x: new Date(row.date).getTime(),
        y: val === null || val === undefined ? 0 : Number(val)
      };
    })
    .filter((p: { x: number; y: number }) => !isNaN(p.x) && !isNaN(p.y))
    .sort((a: { x: number; y: number }, b: { x: number; y: number }) => a.x - b.x);

  if (siteChart) siteChart.destroy();

  siteChart = new Chart(els.siteChartCanvas, {
    type: 'line',
    data: {
      datasets: [{
        label: getParameterName(param),
        data: points,
        borderColor: '#3498db',
        backgroundColor: 'rgba(52, 152, 219, 0.15)',
        borderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        fill: true,
        tension: 0.2,
        spanGaps: false
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      scales: {
        x: {
          type: 'time',
          time: { unit: 'day', tooltipFormat: 'dd.MM.yyyy', displayFormats: { day: 'dd.MM' } },
          title: { display: true, text: 'Дата отбора' },
          grid: { display: false }
        },
        y: {
          beginAtZero: false,
          title: { display: true, text: getParameterName(param) },
          ticks: { maxTicksLimit: 8 }
        }
      },
      plugins: {
        tooltip: {
          backgroundColor: 'rgba(0,0,0,0.8)',
          padding: 10,
          cornerRadius: 4,
          callbacks: {
            label: (ctx) => `${getParameterName(param)}: ${ctx.parsed.y}`,
            title: (items) => {
              if (!items.length) return '';
              const timestamp = items[0].parsed.x;
              return timestamp !== null ? new Date(timestamp).toLocaleDateString('ru-RU') : '';
            }
          }
        },
        legend: { display: false }
      }
    }
  });
}

export function hideSitePanel(): void {
  els.siteDetails.style.display = 'none';
  if (siteChart) {
    siteChart.destroy();
    siteChart = null;
  }
}